import { HttpErrorResponse } from '@angular/common/http';
import { effect, inject, Injectable } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Store } from '@ngrx/store';
import { combineLatest, filter, forkJoin, map, mergeMap, Observable, of, pipe, take, tap } from 'rxjs';
import {
  ApiListTransitionElementResponse,
  ApiProgressResponse,
  ApiTargetResponse,
  ApiTransitionElementResponse,
  ModelsTargetType,
} from '../../api/core/models';
import { AssessmentResponse } from '../../api/core/models/api/assessment-response';
import { DiagramResponse } from '../../api/core/models/api/diagram-response';
import { WatchlistResponse } from '../../api/core/models/api/watchlist-response';
import { TransitionElementService, WatchlistService } from '../../api/core/services';
import { EMPTY_GUID } from '../../constants/empty-guid';
import { injectUntilDestroyed } from '../../functions/inject-functions/inject-until-destroyed';
import { BoardFacadeService } from '../../services/store/board/board-facade.service';
import { selectRouteBoardId } from '../router/router.selectors';
import { ScenariosStore } from '../scenarios/scenarios.store';

type TransitionElementsState = {
  error: Error | HttpErrorResponse | undefined;
  isLoading: boolean;
  transitionElements: ApiTransitionElementResponse[] | undefined;
};

const initialState: TransitionElementsState = {
  error: undefined,
  isLoading: false,
  transitionElements: undefined,
};

@Injectable({ providedIn: 'root' })
export class TransitionElementsStore extends signalStore(
  withState(initialState),
  withMethods((store) => {
    const boardFacadeService = inject(BoardFacadeService);
    const store$ = inject(Store);
    const transitionElementsService = inject(TransitionElementService);
    const watchlistService = inject(WatchlistService);
    return {
      loadTransitionElements: rxMethod<{ scenarioId: string }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          mergeMap(({ scenarioId }) =>
            combineLatest([
              boardFacadeService.getBoardProperty$(['id']),
              boardFacadeService.boardLanguage$,
              store$.select(selectRouteBoardId),
            ]).pipe(
              filter(
                ([boardDataId, language, routeBoardId]) =>
                  Boolean(boardDataId) && Boolean(routeBoardId) && Boolean(language) && boardDataId === routeBoardId
              ),
              take(1),
              mergeMap(
                ([_, language, routeBoardId]): Observable<ApiListTransitionElementResponse> =>
                  transitionElementsService
                    .listTransitionElements({ boardId: routeBoardId!, 'Accept-Language': language!, scenarioId })
                    .pipe(
                      map((res) => ({
                        ...res,
                        transitionElements: res.transitionElements.map((te) => ({
                          ...te,
                          actionIds: te.actionIds ?? [],
                        })),
                      }))
                    )
              ),
              tapResponse({
                next: (response) => {
                  patchState(store, {
                    transitionElements: response.transitionElements,
                    isLoading: false,
                    error: undefined,
                  });
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isLoading: false, error });
                  console.error('Error loading transition elements:', error);
                },
              })
            )
          )
        )
      ),

      // helper method to set transition elements from transition stories store, when adding transition element
      addTransitionElement: rxMethod<ApiTransitionElementResponse>(
        pipe(
          tap((transitionElement: ApiTransitionElementResponse) =>
            patchState(store, {
              transitionElements: [...(store.transitionElements() ?? []), transitionElement],
              isLoading: false,
              error: undefined,
            })
          )
        )
      ),

      // helper method to set transition elements from effect below
      setTransitionElements: rxMethod<ApiTransitionElementResponse[] | undefined>(
        pipe(
          tap((transitionElements: ApiTransitionElementResponse[] | undefined) =>
            patchState(store, { transitionElements: transitionElements, isLoading: false, error: undefined })
          )
        )
      ),

      updateTransitionElement: rxMethod<{
        transitionElementId: string;
        targetType: ModelsTargetType;
      }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          mergeMap(({ transitionElementId, targetType }) =>
            combineLatest([store$.select(selectRouteBoardId)]).pipe(
              filter(([boardId]) => Boolean(boardId)),
              take(1),
              mergeMap(([boardId]): Observable<ModelsTargetType> => {
                return transitionElementsService
                  .updateTransitionElement({
                    boardId: boardId!,
                    id: transitionElementId,
                    body: { targetType },
                  })
                  .pipe(map((_) => targetType));
              }),
              tapResponse({
                next: (targetType) => {
                  patchState(store, {
                    transitionElements: store
                      .transitionElements()
                      ?.map((te) => (te.id === transitionElementId ? { ...te, targetType } : te)),
                    isLoading: false,
                    error: undefined,
                  });
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isLoading: false, error });
                  console.error('Error updating transition element:', error);
                },
              })
            )
          )
        )
      ),

      updateTransitionElementAssessment: rxMethod<{ transitionElementId: string; assessment: AssessmentResponse }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          mergeMap(({ transitionElementId, assessment }) =>
            combineLatest([store$.select(selectRouteBoardId), boardFacadeService.boardLanguage$]).pipe(
              filter(([boardId, boardLang]) => Boolean(boardId) && Boolean(boardLang)),
              take(1),
              mergeMap(
                ([boardId, boardLang]): Observable<AssessmentResponse> =>
                  transitionElementsService
                    .updateAssessment({
                      boardId: boardId!,
                      transitionElementId: transitionElementId,
                      id: assessment.id,
                      body: {
                        about: { [boardLang]: assessment.about! },
                        data: assessment.data,
                        isUnknown: assessment.isUnknown,
                      },
                    })
                    .pipe(map(() => assessment))
              ),
              tapResponse({
                next: (response) => {
                  patchState(store, {
                    transitionElements: store
                      .transitionElements()
                      ?.map((te) => (te.id === transitionElementId ? { ...te, assessment: response } : te)),
                    isLoading: false,
                    error: undefined,
                  });
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isLoading: false, error });
                  console.error('Error updating transition element:', error);
                },
              })
            )
          )
        )
      ),
      updateTransitionElementDiagram: rxMethod<{ transitionElementId: string; diagram: DiagramResponse }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          mergeMap(({ transitionElementId, diagram }) =>
            combineLatest([store$.select(selectRouteBoardId), boardFacadeService.boardLanguage$]).pipe(
              filter(([boardId, boardLang]) => Boolean(boardId) && Boolean(boardLang)),
              take(1),
              mergeMap(
                ([boardId, boardLang]): Observable<DiagramResponse> =>
                  transitionElementsService
                    .updateTransitionElementDiagram({
                      boardId: boardId!,
                      id: transitionElementId,
                      body: {
                        about: { [boardLang]: diagram.about! },
                        reference: { [boardLang]: diagram.reference! },
                        moreInfo: { [boardLang]: diagram.moreInfo! },
                      },
                    })
                    .pipe(map(() => diagram))
              ),
              tapResponse({
                next: (response) => {
                  patchState(store, {
                    transitionElements: store
                      .transitionElements()
                      ?.map((te) => (te.id === transitionElementId ? { ...te, diagram: response } : te)),
                    isLoading: false,
                    error: undefined,
                  });
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isLoading: false, error });
                  console.error('Error updating transition element:', error);
                },
              })
            )
          )
        )
      ),

      updateTransitionElementProgress: rxMethod<{ transitionElementId: string; progress: ApiProgressResponse }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          mergeMap(({ transitionElementId, progress }) =>
            store$.select(selectRouteBoardId).pipe(
              filter((boardId) => Boolean(boardId)),
              take(1),
              mergeMap((boardId): Observable<ApiProgressResponse> => {
                // check if the progress is already in the transition element, if not create otherwise update
                const transitionElement = (store.transitionElements() ?? []).find(
                  (te) => te.id === transitionElementId
                );
                const existingProgress = transitionElement?.progress?.find((p) => p.targetType === progress.targetType);
                if (existingProgress) {
                  return transitionElementsService
                    .updateTransitionElementProgress({
                      boardId: boardId!,
                      targetType: progress.targetType,
                      id: transitionElementId,
                      body: {
                        data: progress.data,
                      },
                    })
                    .pipe(map(() => progress));
                } else {
                  return transitionElementsService.createTransitionElementProgress({
                    boardId: boardId!,
                    id: transitionElementId,
                    body: progress,
                  });
                }
              }),
              tapResponse({
                next: (response) => {
                  const transitionElements = [...(store?.transitionElements() ?? [])];
                  const index = transitionElements.findIndex((te) => te.id === transitionElementId);
                  if (index !== -1 && transitionElements[index]?.progress) {
                    const existingProgress = transitionElements[index]?.progress?.find(
                      (p) => p.targetType === response.targetType
                    );
                    if (existingProgress) {
                      transitionElements[index].progress = transitionElements[index].progress!.map((p) =>
                        p.id === response.id ? response : p
                      );
                    } else {
                      transitionElements[index].progress = [...(transitionElements[index].progress ?? []), response];
                    }
                  }
                  patchState(store, {
                    transitionElements,
                    isLoading: false,
                    error: undefined,
                  });
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isLoading: false, error });
                  console.error('Error updating transition element:', error);
                },
              })
            )
          )
        )
      ),

      updateTransitionElementTarget: rxMethod<{
        transitionElementId: string;
        target: ApiTargetResponse;
      }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          mergeMap(({ transitionElementId, target }) =>
            combineLatest([store$.select(selectRouteBoardId), boardFacadeService.boardLanguage$]).pipe(
              filter(([boardId, language]) => Boolean(boardId) && Boolean(language)),
              take(1),
              mergeMap(
                ([boardId, boardLang]): Observable<ApiTargetResponse> =>
                  transitionElementsService
                    .updateTarget({
                      boardId: boardId!,
                      transitionElementId: transitionElementId,
                      id: target.id,
                      body: {
                        goalDescription: { [boardLang]: target.goalDescription! },
                        targetLevel: target.targetLevel,
                        displayTargetLevel: target.displayTargetLevel,
                        targets: target.targets,
                        curveType: target.curveType,
                      },
                    })
                    .pipe(map(() => target))
              ),
              tapResponse({
                next: (response) => {
                  const transitionElements = [...(store.transitionElements() ?? [])];
                  const index = transitionElements.findIndex((te) => te.id === transitionElementId);
                  if (index !== -1 && transitionElements[index]?.target) {
                    transitionElements[index].target = response;
                  }
                  patchState(store, {
                    transitionElements,
                    isLoading: false,
                    error: undefined,
                  });
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isLoading: false, error });
                  console.error('Error updating transition element:', error);
                },
              })
            )
          )
        )
      ),

      updateTransitionElementOtherScenarioTargets: rxMethod<{
        transitionElementId: string;
        targets: { [key: string]: ApiTargetResponse };
      }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          mergeMap(({ transitionElementId, targets }) =>
            store$.select(selectRouteBoardId).pipe(
              filter((boardId) => Boolean(boardId)),
              take(1),
              mergeMap((boardId): Observable<string[]> => {
                if (Object.keys(targets).length === 0) {
                  return of([]);
                }
                return forkJoin(
                  Object.entries(targets).map(([targetId, target]) =>
                    transitionElementsService.updateTarget({
                      boardId: boardId!,
                      transitionElementId: transitionElementId,
                      id: targetId,
                      body: {
                        curveType: target.curveType,
                        displayTargetLevel: target.displayTargetLevel,
                        targetLevel: target.targetLevel,
                        targets: target.targets,
                      },
                    })
                  )
                );
              }),
              tapResponse({
                next: (response) => {
                  patchState(store, {
                    isLoading: false,
                    error: undefined,
                  });
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isLoading: false, error });
                  console.error('Error updating transition element:', error);
                },
              })
            )
          )
        )
      ),

      updateTransitionElementWatchlist: rxMethod<{
        transitionElementId: string;
        watchlist: WatchlistResponse;
        shouldDeleteWatchlist: boolean;
      }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          mergeMap(({ transitionElementId, watchlist, shouldDeleteWatchlist }) =>
            store$.select(selectRouteBoardId).pipe(
              filter((boardId) => Boolean(boardId)),
              take(1),
              mergeMap((boardId): Observable<WatchlistResponse | undefined> => {
                if (watchlist.id && watchlist.id !== EMPTY_GUID) {
                  if (shouldDeleteWatchlist) {
                    return watchlistService
                      .deleteWatchlist({
                        boardId: boardId!,
                        id: watchlist.id,
                      })
                      .pipe(map(() => undefined));
                  }
                  return watchlistService
                    .updateWatchlist({
                      boardId: boardId!,
                      id: watchlist.id,
                      body: {
                        // @ts-ignore
                        ...watchlist,
                        owner: 'transition_elements',
                        ownerId: transitionElementId,
                      },
                    })
                    .pipe(map(() => watchlist));
                } else {
                  return watchlistService.createWatchlist({
                    boardId: boardId!,
                    body: {
                      // @ts-ignore
                      ...watchlist,
                      owner: 'transition_elements',
                      ownerId: transitionElementId,
                    },
                  });
                }
              }),
              tapResponse({
                next: (response) => {
                  patchState(store, {
                    transitionElements: store
                      .transitionElements()
                      ?.map((te) => (te.id === transitionElementId ? { ...te, watchlist: response } : te)),
                    isLoading: false,
                    error: undefined,
                  });
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isLoading: false, error });
                  console.error('Error updating transition element:', error);
                },
              })
            )
          )
        )
      ),

      updateTransitionElementActionIds: rxMethod<{ transitionElementIds: string[]; actionId: string }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          map(({ transitionElementIds, actionId }) => {
            const transitionElements: ApiTransitionElementResponse[] = [...(store.transitionElements() ?? [])].map(
              (te): ApiTransitionElementResponse => {
                if (transitionElementIds.includes(te.id)) {
                  const existingActionIds = te.actionIds ?? [];
                  const newActionIds = Array.from(new Set([...existingActionIds, actionId]));
                  return {
                    ...te,
                    actionIds: newActionIds,
                  };
                } else {
                  return {
                    ...te,
                    actionIds: te.actionIds?.filter((id) => id !== actionId),
                  };
                }
              }
            );
            patchState(store, {
              transitionElements,
              isLoading: false,
              error: undefined,
            });
          })
        )
      ),
    };
  }),
  withHooks({
    onInit: (store) => {
      const untilDestroyed = injectUntilDestroyed();
      // Reset state when board changes
      inject(Store)
        .select(selectRouteBoardId)
        .pipe(untilDestroyed())
        .subscribe(() => {
          patchState(store, initialState);
        });
    },
  })
) {
  private _scenariosStore = inject(ScenariosStore);
  private _selectedScenarioId: string | undefined = undefined;

  constructor() {
    super();

    // Effect to handle scenario changes
    effect(() => {
      const selectedScenario = this._scenariosStore.selectedScenario();
      if (selectedScenario) {
        if (selectedScenario?.id !== this._selectedScenarioId) {
          this._selectedScenarioId = selectedScenario.id;
          this.setTransitionElements(undefined);
          this.loadTransitionElements({ scenarioId: selectedScenario.id });
          this._selectedScenarioId = selectedScenario.id;
        }
      } else {
        this._selectedScenarioId = undefined;
      }
    });
  }
}
